import type {
  GetMainMenuItemsParams,
  GridApi,
  MenuItemDef
} from "ag-grid-enterprise";
import { renderToStaticMarkup } from "react-dom/server";
import { getHeaderSortMenuItems } from "components/ciq-ag-grid/header-sort-menu";
import ConditionalFormattingIcon from "components/svg-icons/conditional-formatting-icon";

export const OPEN_CONDITIONAL_FORMATTING_EVENT =
  "ciq-open-conditional-formatting";

export const openConditionalFormattingForColumn = (
  api: GridApi,
  columnId: string
) => {
  api.hidePopupMenu();
  window.dispatchEvent(
    new CustomEvent(OPEN_CONDITIONAL_FORMATTING_EVENT, {
      detail: { columnId, api }
    })
  );
};

export type ButtonCustomMenuItemProps = {
  api: GridApi;
  columnId?: string;
};

const EXCLUDED_COLUMN_IDS = new Set([
  "ag-Grid-SelectionColumn",
  "bidding_subcontractors",
  "linked_design_packages",
  "linked_tasks",
  "linked_materials",
  "linked_submittals",
  "linked_activities",
  "locations",
  "action",
  // Hierarchy group-only leaf counts — not a meaningful per-row CF target.
  "scopeItemCount"
]);

const CONDITIONAL_FORMATTING_ICON = renderToStaticMarkup(
  <ConditionalFormattingIcon />
);

export const getConditionalFormattingHeaderMenuItems = (
  params: GetMainMenuItemsParams
): MenuItemDef[] => {
  const sortMenuItems = getHeaderSortMenuItems(params);
  const { api, column } = params;
  const columnId = column?.getColId();
  if (!column || !columnId || columnId === "ag-Grid-SelectionColumn") {
    return [];
  }

  const colDef = column.getColDef();
  const isGroupByColumn =
    columnId.includes("ag-Grid-AutoColumn") ||
    !!colDef.showRowGroup ||
    colDef.rowGroup === true ||
    column.isRowGroupActive?.() === true ||
    api
      .getRowGroupColumns?.()
      .some((rowGroupColumn) => rowGroupColumn.getColId() === columnId);
  const excludeConditionalFormatting =
    isGroupByColumn ||
    colDef.suppressColumnsToolPanel === true ||
    EXCLUDED_COLUMN_IDS.has(columnId);
  if (excludeConditionalFormatting) return sortMenuItems;

  const liveContext = (
    typeof api.getGridOption === "function"
      ? api.getGridOption("context")
      : undefined
  ) as {
    conditionalFormattingRules?: { columnId: string }[];
  };
  const rulesForColumn = liveContext?.conditionalFormattingRules ?? [];
  const hasConditionalFormatting = rulesForColumn.some(
    (r) => r.columnId === columnId
  );

  return [
    ...sortMenuItems,
    ...(hasConditionalFormatting
      ? ([
          {
            name: "Edit Conditional Formatting",
            icon: CONDITIONAL_FORMATTING_ICON,
            action: () => openConditionalFormattingForColumn(api, columnId)
          }
        ] as MenuItemDef[])
      : ([
          {
            name: "Conditional Formatting",
            icon: CONDITIONAL_FORMATTING_ICON,
            action: () => openConditionalFormattingForColumn(api, columnId)
          }
        ] as MenuItemDef[]))
  ];
};
